import React, { useEffect, useRef, useState } from 'react';
import { webRTCService } from '../../services/webRTCService.ts';
import styles from './VideoCall.module.css';

interface VideoCallProps {
  isUser1: boolean;
  matchId: string;
  onClose: () => void;
}

export const VideoCall: React.FC<VideoCallProps> = ({ matchId, isUser1, onClose }) => {
  const localVideoRef = useRef<HTMLVideoElement>(null);
  const remoteVideoRef = useRef<HTMLVideoElement>(null);
  const [isRemoteStreamReady, setIsRemoteStreamReady] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isRetrying, setIsRetrying] = useState(false);

  const handleLocalStream = (stream: MediaStream) => {
    if (localVideoRef.current) {
      localVideoRef.current.srcObject = stream;
      const playPromise = localVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Error playing local video:', error);
          setError('Failed to start local video');
          // Retry playing after a short delay
          setTimeout(() => {
            if (localVideoRef.current) {
              localVideoRef.current.play().catch(console.warn);
            }
          }, 1000);
        });
      }
    }
  };

  const handleRemoteStream = (stream: MediaStream) => {
    if (remoteVideoRef.current) {
      console.log('Setting remote stream:', stream.getTracks().map(t => t.kind));
      
      // Ensure the video element is ready
      if (remoteVideoRef.current.readyState === 0) {
        remoteVideoRef.current.onloadedmetadata = () => {
          console.log('Remote video metadata loaded');
          const playPromise = remoteVideoRef.current?.play();
          if (playPromise !== undefined) {
            playPromise.catch(error => {
              console.warn('Error playing remote video:', error);
              //setError('Failed to play remote video');
              // Retry playing after a short delay
              setTimeout(() => {
                if (remoteVideoRef.current) {
                  remoteVideoRef.current.play().catch(console.warn);
                }
              }, 1000);
            });
          }
        };
      }

      // Only update the stream if it's different
      if (remoteVideoRef.current.srcObject !== stream) {
        remoteVideoRef.current.srcObject = stream;
        setIsRemoteStreamReady(true);
        setError(null);
        
        // Ensure the video plays
        const playPromise = remoteVideoRef.current.play();
        if (playPromise !== undefined) {
          playPromise.catch(error => {
            console.warn('Error playing remote video:', error);
            //setError('Failed to play remote video');
            // Retry playing after a short delay
            setTimeout(() => {
              if (remoteVideoRef.current) {
                remoteVideoRef.current.play().catch(console.warn);
              }
            }, 1000);
          });
        }
      }
    }
  };

  const startVideoCall = async () => {
    try {
      setIsRetrying(false);
      setError(null);
      const localStream = await webRTCService.startCall(matchId, isUser1);
      handleLocalStream(localStream);
    } catch (error) {
      console.error('Error starting video call:', error);
      setError('Failed to start video call');
    }
  };

  const retryConnection = async () => {
    if (isRetrying) return;
    
    setIsRetrying(true);
    setError('Retrying connection...');
    
    try {
      await webRTCService.endCall();
      await startVideoCall();
    } catch (error) {
      console.error('Error retrying connection:', error);
      setError('Failed to retry connection');
    } finally {
      setIsRetrying(false);
    }
  };

  useEffect(() => {
    const handleRemoteStreamEvent = (event: CustomEvent<{ 
      stream: MediaStream;
      trackKind: string;
      trackId: string;
    }>) => {
      console.log('Remote stream available:', event.detail);
      handleRemoteStream(event.detail.stream);
    };

    document.addEventListener('remoteStreamAvailable', handleRemoteStreamEvent as EventListener);
    startVideoCall();

    return () => {
      document.removeEventListener('remoteStreamAvailable', handleRemoteStreamEvent as EventListener);
      webRTCService.endCall();
    };
  }, [matchId]);

  // Handle remote video playback
  useEffect(() => {
    if (isRemoteStreamReady && remoteVideoRef.current) {
      console.log('Remote stream ready, attempting to play');
      const playPromise = remoteVideoRef.current.play();
      if (playPromise !== undefined) {
        playPromise.catch(error => {
          console.warn('Error playing remote video:', error);
          //setError('Failed to play remote video');
          // Retry playing after a short delay
          setTimeout(() => {
            if (remoteVideoRef.current) {
              remoteVideoRef.current.play().catch(console.warn);
            }
          }, 1000);
        });
      }
    }
  }, [isRemoteStreamReady]);

  return (
    <div className={styles.videoCallContainer}>
      {error && (
        <div className={styles.errorContainer}>
          <p>{error}</p>
          <button 
            className={styles.retryButton}
            onClick={retryConnection}
            disabled={isRetrying}
          >
            {isRetrying ? 'Retrying...' : 'Retry Connection'}
          </button>
        </div>
      )}
      <div className={styles.videoGrid}>
        <div className={styles.videoWrapper}>
          <video
            ref={localVideoRef}
            autoPlay
            playsInline
            muted
            className={styles.videoElement}
          />
          <div className={styles.videoLabel}>You</div>
        </div>
        <div className={styles.videoWrapper}>
          <video
            ref={remoteVideoRef}
            autoPlay
            playsInline
            className={styles.videoElement}
            onLoadedMetadata={() => console.log('Remote video metadata loaded')}
            onError={(e) => console.error('Remote video error:', e)}
          />
          <div className={styles.videoLabel}>Match</div>
        </div>
      </div>
      <button className={styles.endCallButton} onClick={onClose}>
        End Call
      </button>
    </div>
  );
};
